import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFilter, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import List from "../../../components/List/List"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { companyMemory, manualExternalOrderMemory } from "../../../Objects/Memory/Memory"
import { ManualExternalOrder } from "../../../Objects/ManualExternalOrder/ManualExternalOrder"
import { ManualExternalOrderFilter } from "../../../Objects/ManualExternalOrder/ManualExternalOrderFilter"
import { createEmptyManualExternalOrder } from "../../../Objects/ManualExternalOrder/manualExternalOrderHelpers"
import { useManualExternalOrders } from "../../../hooks/ManualExternalOrder/useManualExternalOrders"
import { getDateKeyInTimeZone } from "../../../utils/timeZone"
import { ManualExternalOrderFiltersPopup } from "./ManualExternalOrderFiltersPopup"
import { ManualExternalOrderPopup } from "./ManualExternalOrderPopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

const currentDate = () => getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId)

const createDefaultFilter = (): ManualExternalOrderFilter => ({
  dateStart: currentDate(),
  dateEnd: currentDate(),
  sourceType: "",
  status: "",
  reconciliationStatus: "",
  paymentMethod: "",
  deliveryType: "",
  minAmount: "",
  maxAmount: "",
  search: "",
})

export const ManualExternalOrdersPage: React.FC = () => {
  const { t } = useTranslation("manualExternalOrder")

  const location = useLocation()

  const { setNewConfirmation } = useConfirmation()

  const { handleGetManualExternalOrders, handleGetManualExternalOrder, handleCancelManualExternalOrder } =
    useManualExternalOrders()

  const [filter, setFilter] = useState<ManualExternalOrderFilter>(createDefaultFilter)

  const [refreshKey, setRefreshKey] = useState(0)

  const [editingOrder, setEditingOrder] = useState<ManualExternalOrder | undefined>()

  const [showPopup, setShowPopup] = useState(false)

  const [showFiltersPopup, setShowFiltersPopup] = useState(false)

  useQuickActionPopup("manual-external-orders-filters", () => setShowFiltersPopup(true))

  const load = (nextFilter = filter) => {
    void handleGetManualExternalOrders(nextFilter, () => setRefreshKey((current) => current + 1))
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    if (searchParams.get("onboardingCreate") === "true") {
      handleOpenCreate()
    }
  }, [location.search])

  useEffect(() => {
    const closePopups = () => {
      setShowPopup(false)
      setShowFiltersPopup(false)
    }

    window.addEventListener("close-owner-onboarding-popups", closePopups)

    return () => window.removeEventListener("close-owner-onboarding-popups", closePopups)
  }, [])

  const handleApplyFilters = (nextFilter: ManualExternalOrderFilter) => {
    setFilter(nextFilter)
    load(nextFilter)
  }

  const handleClearFilters = () => {
    const nextFilter = createDefaultFilter()
    setFilter(nextFilter)
    load(nextFilter)
  }

  const handleOpenCreate = () => {
    setEditingOrder(createEmptyManualExternalOrder())
    setShowPopup(true)
  }

  const handleOpenEdit = (id: number) => {
    handleGetManualExternalOrder(id, (data) => {
      setEditingOrder(data)
      setShowPopup(true)
    })
  }

  const handleCancel = (order: ManualExternalOrder) => {
    setNewConfirmation(t("confirm.cancel_title"), t("confirm.cancel_message"), () =>
      handleCancelManualExternalOrder(order.id, () => {
        setShowPopup(false)
        load()
      }),
    )
  }

  return (
    <div className={defaultStyle.containerList}>
      <List
        key={refreshKey}
        data={manualExternalOrderMemory.getListCards(true)}
        messageCount={t("list.count")}
        messageEmpty={t("list.empty")}
        nameFindSearch={t("filters.local_search")}
        handleAddButton={handleOpenCreate}
        handleRefreshButton={() => load()}
        handleExtraFloatButton={() => setShowFiltersPopup(true)}
        extraFloatButtonDescriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrdersPage.button.openFilters"
        extraFloatButtonIcon={faFilter}
        extraFloatButtonColor="#fb923c"
        showFilterStatus={false}
        handleOnClick={handleOpenEdit}
      />

      {showFiltersPopup && (
        <ManualExternalOrderFiltersPopup
          filter={filter}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onClose={() => setShowFiltersPopup(false)}
        />
      )}

      {showPopup && editingOrder && (
        <ManualExternalOrderPopup
          order={editingOrder.id > 0 ? editingOrder : undefined}
          onClose={() => setShowPopup(false)}
          onSave={() => load()}
          onCancelOrder={handleCancel}
        />
      )}
    </div>
  )
}
